import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "semantic-ui-react";
import { confirmbox_close } from "./reducer/actions";

import Title from "./bits/Title";
import Description from "./bits/Description";

import { capitalize } from "../../app_shared_functions";
import { hasFIP } from "../../openstack/servers/utils";

import CascadeLBDelete from "./bits/secondaryOptions/CascadeLBDelete";
import CascadeNetworkDelete from "./bits/secondaryOptions/CascadeNetworkDelete";
import DRRestoreType from "./bits/secondaryOptions/DRRestoreType";
import RebootType from "./bits/secondaryOptions/RebootType";
import RemoveFIP from "./bits/secondaryOptions/RemoveFIP";
import { actionMap } from "./reducer/actionsMap";
import { State } from "../../selectors/state";
import { ConfirmBoxOptionsType } from "./reducer/type";

const ConfirmBox = () => {
  const [options, setOptions] = useState<ConfirmBoxOptionsType>({
    reboot: "soft",
    cascade: false,
    cascadeNetwork: false,
    autostart: false,
    removeFIP: false,
  });

  const data = useSelector((state: State) => state.confirmbox);
  const { entity, operation, resources, open } = data;
  const dispatch = useDispatch();

  const handleCancel = () => dispatch(confirmbox_close());

  const handleConfirm = () => {
    if (!entity || !operation) return;

    const actionFn = Array.isArray(resources)
      ? actionMap[entity]?.["multiple"]?.[operation]
      : actionMap[entity]?.["single"]?.[operation];
    if (actionFn) {
      dispatch(actionFn(resources, options));
    }

    handleCancel();
  };

  /**
   * Checks the list of servers (or a single server presented by an object) has floating ip attached to them
   * @returns true: if at least one of the servers has at least one floating ip attached to
   *          false: if all of the servers have zero floating ip attached to
   */
  const hasFloatingIPsAttachedToServers = useMemo(() => {
    if (Array.isArray(resources)) {
      return resources.some(hasFIP);
    } else {
      return hasFIP(resources);
    }
  }, [resources]);

  const showSecondaryOptions = useMemo(
    () => ({
      cascadeLoadBalancerDelete:
        operation === "delete" && entity === "loadbalancer",
      cascadeNetworkDelete: operation === "delete" && entity === "network",
      disasterRecoverRestoreType:
        operation === "restore snapshot" && entity === "server",
      rebootType: operation === "reboot" && entity === "server",
      removeFloatingIP:
        operation === "delete" &&
        entity === "server" &&
        hasFloatingIPsAttachedToServers,
    }),
    [entity, operation, hasFloatingIPsAttachedToServers],
  );

  if (!entity || !operation || !resources) return null;

  return (
    <Modal dimmer="blurring" open={open} onClose={handleCancel}>
      <Title entity={entity} operation={operation} resources={resources} />

      <Modal.Content>
        <Description
          entity={entity}
          operation={operation}
          resources={resources}
        />
      </Modal.Content>

      <Modal.Actions>
        <button
          className="float-left button button--bordered"
          onClick={handleCancel}
        >
          <span>No</span>
        </button>

        {showSecondaryOptions.cascadeLoadBalancerDelete && (
          <CascadeLBDelete options={options} setOptions={setOptions} />
        )}

        {showSecondaryOptions.cascadeNetworkDelete && (
          <CascadeNetworkDelete options={options} setOptions={setOptions} />
        )}

        {showSecondaryOptions.disasterRecoverRestoreType && (
          <DRRestoreType options={options} setOptions={setOptions} />
        )}

        {showSecondaryOptions.rebootType && (
          <RebootType options={options} setOptions={setOptions} />
        )}

        {showSecondaryOptions.removeFloatingIP && (
          <RemoveFIP options={options} setOptions={setOptions} />
        )}

        <button
          className="float-right button button--red "
          onClick={handleConfirm}
        >
          <span>Yes, {capitalize(operation)}</span>
        </button>
      </Modal.Actions>
    </Modal>
  );
};

export default ConfirmBox;
